import { Controller } from "@hotwired/stimulus"

// Reveals elements as they scroll into view. Any element tagged with
// `data-reveal` fades/slides in once. Its animation order is staggered
// relative to the nearest `[data-reveal-group]` ancestor, so each cluster
// (a header block, a row of cards, a feature row) animates as a unit and the
// stagger restarts cleanly for the next cluster instead of accumulating down
// the whole page.
//
// Per-element options:
//   data-reveal="up|down|left|right|scale|fade"  direction (default: up)
//   data-reveal-delay="120"                       explicit delay in ms (overrides stagger)
//
// Per-group options (on a [data-reveal-group] ancestor):
//   data-reveal-stagger="90"                      ms between siblings (default 90)
//
// The scroll root is the dashboard's `#content-container` when present (the
// marketing pages render inside that scrolling <main>), otherwise the viewport.
export default class extends Controller {
  connect() {
    this.items = Array.from(this.element.querySelectorAll("[data-reveal]"))
    if (this.items.length === 0) return

    this.prepareItems()

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduceMotion || !("IntersectionObserver" in window)) {
      this.items.forEach((item) => item.classList.add("reveal-visible"))
      return
    }

    const root = document.getElementById("content-container") || null

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          entry.target.classList.add("reveal-visible")
          this.observer.unobserve(entry.target)
        })
      },
      { root, rootMargin: "0px 0px -12% 0px", threshold: 0.05 }
    )

    this.items.forEach((item) => this.observer.observe(item))
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
  }

  prepareItems() {
    // Per-group running index → staggered delay that restarts for each cluster.
    const counters = new Map()

    this.items.forEach((item) => {
      const variant = item.getAttribute("data-reveal")
      item.classList.add("reveal-item")
      if (variant && variant !== "up") item.classList.add(`reveal-${variant}`)

      const group = item.closest("[data-reveal-group]") || this.element
      const index = counters.get(group) || 0
      counters.set(group, index + 1)

      const override = item.getAttribute("data-reveal-delay")
      let delay
      if (override !== null) {
        delay = parseInt(override, 10) || 0
      } else {
        const stagger = parseInt(group.getAttribute("data-reveal-stagger"), 10) || 90
        // Cap the stagger depth so long card grids never sit idle behind a
        // multi-hundred-millisecond delay once they scroll into view.
        delay = Math.min(index, 5) * stagger
      }

      item.style.setProperty("--reveal-delay", `${delay}ms`)
    })
  }
}
